import React, { useEffect, useState } from "react";
import {
    FaCheckCircle,
    FaChevronRight,
    FaClipboardList,
    FaClock,
    FaExclamationTriangle,
    FaToolbox,
} from "react-icons/fa";
import { reportResource } from "../../helpers/configRoutes/backend_helper";

const ServiceRequest = () => {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchRequests = async () => {
            try {
                const response = await reportResource("get", { params: { perPage: 100 } });
                setRequests(response.reports || []);
            } catch (error) {
                console.error("Error loading service requests", error);
            } finally {
                setLoading(false);
            }
        };

        fetchRequests();
    }, []);

    const getStatusTone = (request) => {
        if (request.service_order_id) {
            return {
                badge: "bg-cs-success/10 text-cs-success border-cs-success/20",
                icon: "bg-cs-success text-white",
                helper: "Already attended by maintenance",
            };
        }

        return {
            badge: "bg-cs-primary-soft text-cs-primary border-cs-primary/20",
            icon: "bg-cs-danger text-white",
            helper: "Waiting for maintenance review",
        };
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-cs-bg-alt">
                <p className="text-cs-muted text-base">Loading service requests…</p>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-cs-bg-alt pb-24 font-sans">
            <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-md px-4 py-5 border-b border-cs-border">
                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-[10px] uppercase tracking-[0.2em] text-cs-muted font-bold">
                            Maintenance
                        </p>
                        <h1 className="text-lg font-bold text-cs-dark tracking-tight">
                            My Service Requests
                        </h1>
                    </div>
                    <div className="w-11 h-11 rounded-2xl bg-cs-primary-soft flex items-center justify-center text-cs-primary">
                        <FaClipboardList size={18} />
                    </div>
                </div>
            </header>

            <div className="p-4 flex flex-col gap-4">
                <div className="bg-white rounded-[2rem] shadow-soft border border-white p-4">
                    <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-2xl bg-cs-primary-soft text-cs-primary flex items-center justify-center shrink-0">
                            <FaExclamationTriangle size={16} />
                        </div>
                        <div>
                            <p className="text-sm font-bold text-cs-dark">Informational history</p>
                            <p className="text-xs text-cs-muted leading-relaxed mt-1">
                                This section shows every maintenance request you have submitted.
                                New requests can only be created from an active load.
                            </p>
                        </div>
                    </div>
                </div>

                <div className="flex items-center justify-between px-1">
                    <h3 className="text-[11px] font-bold text-cs-muted uppercase tracking-[0.2em]">
                        Submitted Requests
                    </h3>
                    <span className="text-[10px] font-bold text-cs-primary bg-white px-2.5 py-1 rounded-lg border border-cs-primary/10 shadow-sm">
                        {requests.length} Total
                    </span>
                </div>

                {requests.length > 0 ? (
                    <div className="space-y-3">
                        {requests.map((request) => {
                            const tone = getStatusTone(request);

                            return (
                                <div
                                    key={request.id}
                                    className="bg-white p-4 rounded-[2rem] shadow-soft border border-white"
                                >
                                    <div className="flex items-start gap-4">
                                        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 ${tone.icon}`}>
                                            {request.service_order_id ? (
                                                <FaCheckCircle size={18} />
                                            ) : (
                                                <FaClock size={18} />
                                            )}
                                        </div>

                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-start justify-between gap-3">
                                                <div>
                                                    <p className="text-sm font-extrabold text-cs-dark tracking-tight">
                                                        Request #{request.id}
                                                    </p>
                                                    <p className="text-[11px] text-cs-muted font-medium mt-1 leading-relaxed break-words">
                                                        {request.description}
                                                    </p>
                                                </div>
                                                <FaChevronRight size={12} className="text-cs-border mt-1 shrink-0" />
                                            </div>

                                            <div className="flex flex-wrap gap-2 mt-3">
                                                <span className={`px-2.5 py-1 rounded-full border text-[10px] font-black uppercase tracking-wide ${tone.badge}`}>
                                                    {request.status?.name || "Pending"}
                                                </span>

                                                {request.stop_id ? (
                                                    <span className="px-2.5 py-1 rounded-full border border-cs-border bg-cs-bg-alt text-[10px] font-bold text-cs-muted uppercase tracking-wide">
                                                        Stop #{request.stop_id}
                                                    </span>
                                                ) : null}
                                            </div>

                                            <div className="mt-4 grid grid-cols-1 gap-2 text-xs">
                                                <div className="flex items-center justify-between gap-4">
                                                    <span className="text-cs-muted font-semibold">Current status</span>
                                                    <span className="text-cs-dark font-bold text-right">{tone.helper}</span>
                                                </div>

                                                <div className="flex items-center justify-between gap-4">
                                                    <span className="text-cs-muted font-semibold">Created at</span>
                                                    <span className="text-cs-dark font-bold text-right">{request.created_at || "--"}</span>
                                                </div>

                                                <div className="flex items-center justify-between gap-4">
                                                    <span className="text-cs-muted font-semibold">Maintenance order</span>
                                                    <span className="text-cs-dark font-bold text-right">
                                                        {request.service_order ? (
                                                            <span className="inline-flex items-center gap-2">
                                                                <FaToolbox className="text-cs-primary" />
                                                                {request.service_order.internalId || `#${request.service_order.id}`}
                                                            </span>
                                                        ) : (
                                                            "Not assigned yet"
                                                        )}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="bg-white rounded-[2rem] shadow-soft border border-white p-10 text-center">
                        <div className="w-16 h-16 rounded-full bg-cs-bg-alt flex items-center justify-center text-cs-border mx-auto mb-4">
                            <FaClipboardList size={24} />
                        </div>
                        <p className="text-sm font-bold text-cs-dark">No requests submitted yet</p>
                        <p className="text-xs text-cs-muted mt-1 leading-relaxed">
                            When you report an issue from an active load, it will appear here.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ServiceRequest;
